import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { config, yup } from 'data';
import { formatter, url } from 'helpers';
import { authService } from 'services';
import { useCountdown, useForm, useLang, useMutation } from 'hooks';
import { PageLayout } from 'layouts';
import { Button, Flex, Form, Input, Paragraph, Progress, Title, Trans } from 'components/ui';
import { AuthRegistrationConfirmParams } from 'types/services';

import styles from './styles.module.css';

type FormValues = AuthRegistrationConfirmParams;

const initialValues: FormValues = {
  email: '',
  password: '',
  code: '',
};

const validationSchema = yup.object({
  email: yup.string().required().email(),
  password: yup.string().required().password(),
  code: yup.string().defined(),
});

const confirmationValidationSchema = validationSchema.shape({
  code: yup.string().required().trim().length(config.VERIFICATION_CODE_LENGTH),
});

const RegistrationPage = () => {
  const countdown = useCountdown(config.VERIFICATION_CODE_RESEND_TIME);
  const form = useForm<FormValues>();
  const lang = useLang();
  const navigate = useNavigate();

  const [email, setEmail] = useState(initialValues.email);

  const registerMutation = useMutation({
    mutationFn: authService.register,
    onMutate: () => countdown.restart(),
  });

  const registerVerifyMutation = useMutation({
    mutationFn: authService.registerVerify,
    onMutate: () => countdown.restart(),
    successNotification: lang.get('registration.verifySuccess'),
  });

  const registerConfirmMutation = useMutation({
    mutationFn: authService.registerConfirm,
    onSuccess: () => navigate(url.toLogin()),
    successNotification: lang.get('registration.confirmSuccess'),
  });

  const handleResendClick = async () => email && registerVerifyMutation.mutateAsync({ email });

  const handleBackClick = () => {
    form.resetFields(['code']);

    setEmail(initialValues.email);
  };

  const handleSubmit = async (values: FormValues) => {
    values.code
      ? await registerConfirmMutation.mutateAsync(values)
      : await registerMutation.mutateAsync(values);

    setEmail(values.email);

    return false;
  };

  const confirmation = Boolean(email);

  return (
    <PageLayout title={lang.get('registration.title')}>
      <Progress className={styles.progress} percent={confirmation ? 100 : 50} />
      <Title>{confirmation ? lang.get('registration.confirmHeading') : lang.get('registration.heading')}</Title>
      <Paragraph>
        <Trans
          transKey={confirmation ? 'registration.confirmCaption' : 'registration.caption'}
          components={{
            Email: <strong>{email}</strong>,
            Link: <Link to={url.toLogin()} />,
          }}
          replace={{ codeLength: config.VERIFICATION_CODE_LENGTH, email }}
        />
      </Paragraph>
      <Form
        className={styles.form}
        form={form}
        initialValues={initialValues}
        validationSchema={confirmation ? confirmationValidationSchema : validationSchema}
        onFinish={handleSubmit}
      >
        <Form.Item
          name="code"
          label={lang.get('common.form.code.label')}
          extra={
            countdown.counting
              ? (
                lang.get('common.form.resend.timer', {
                  time: formatter.formatTime(countdown.time, { showMinutes: true }),
                })
              ) : (
                <Button
                  className={styles.resend}
                  type="link"
                  loading={registerVerifyMutation.isPending}
                  onClick={handleResendClick}
                >
                  {lang.get('common.form.resend.button')}
                </Button>
              )
          }
          hidden={!confirmation}
        >
          <Input
            placeholder={lang.get('common.form.code.placeholder')}
            minLength={config.VERIFICATION_CODE_LENGTH}
            maxLength={config.VERIFICATION_CODE_LENGTH}
          />
        </Form.Item>
        <Form.Item
          name="email"
          label={lang.get('common.form.email.label')}
          hidden={confirmation}
        >
          <Input.Email placeholder={lang.get('common.form.email.placeholder')} />
        </Form.Item>
        <Form.Item
          name="password"
          label={lang.get('common.form.password.label')}
          extra={lang.get('common.form.password.helper', { min: config.PASSWORD_MIN_LENGTH })}
          hidden={confirmation}
        >
          <Input.Password placeholder={lang.get('common.form.password.placeholder')} />
        </Form.Item>
        <Form.Item hidden={confirmation}>
          <Paragraph className={styles.agreement}>
            <Trans
              transKey="registration.agreement"
              components={{
                PrivacyLink: <Link to={url.toPrivacy()} target="_blank" />,
                TermsLink: <Link to={url.toTerms()} target="_blank" />,
              }}
            />
          </Paragraph>
        </Form.Item>
        <Flex gap="small" justify="flex-end">
          <Button
            type="default"
            hidden={!confirmation}
            onClick={handleBackClick}
          >
            {lang.get('common.actions.back')}
          </Button>
          <Button
            htmlType="submit"
            loading={registerMutation.isPending || registerConfirmMutation.isPending}
          >
            {confirmation ? lang.get('common.actions.continue') : lang.get('registration.submit')}
          </Button>
        </Flex>
      </Form>
    </PageLayout>
  );
};

export default RegistrationPage;
